import { get } from 'svelte/store';
import { serviceStore, parseNotice, serviceState, noticeIsActive } from '../stores/serviceStore';
import { API_BASE_URL } from './config';
import { app } from '../firebase';

const cacheKey = 'erion.service_notice.v1';
let remoteNotice = null;
let serverNotice = null;
let ready = null;
let pending = null;
let lastRemoteFetch = 0;
function chooseNotice() {
  if (noticeIsActive(remoteNotice)) return remoteNotice;
  if (!serverNotice) return remoteNotice;
  if (noticeIsActive(serverNotice) || !remoteNotice?.updated_at) return serverNotice;
  return Date.parse(serverNotice.updated_at || '') > Date.parse(remoteNotice.updated_at) ? serverNotice : remoteNotice;
}
function publish() {
  const previous = get(serviceStore).phase;
  const state = serviceState(ready, chooseNotice());
  serviceStore.set(state);
  if (state.phase === 'available' && ['maintenance', 'unavailable'].includes(previous)) {
    document.dispatchEvent(new CustomEvent('serviceRecovered'));
  }
}
function applyRemote(value) {
  const notice = parseNotice(value);
  if (!notice || (remoteNotice?.updated_at && (!notice.updated_at || Date.parse(notice.updated_at) < Date.parse(remoteNotice.updated_at)))) return;
  remoteNotice = notice;
  try { localStorage.setItem(cacheKey, JSON.stringify(notice)); } catch {}
  publish();
}
async function loadRemote(force) {
  if (!force && Date.now() - lastRemoteFetch < 300000) return;
  lastRemoteFetch = Date.now();
  try {
    const remote = await import('firebase/remote-config');
    if (!await remote.isSupported()) return;
    const config = remote.getRemoteConfig(app);
    config.settings = { fetchTimeoutMillis: 5000, minimumFetchIntervalMillis: force ? 10000 : 300000 };
    await remote.fetchAndActivate(config);
    const value = remote.getValue(config, 'erion_service_notice');
    if (value.getSource() === 'remote' && value.asString()) applyRemote(JSON.parse(value.asString()));
  } catch { /* A cached announcement remains usable while the backend is offline. */ }
}
export function refreshService({ force = false } = {}) {
  if (pending) return pending;
  pending = Promise.allSettled([
    loadRemote(force),
    (async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/system/health`, { cache: 'no-store', signal: AbortSignal.timeout(8000) });
        const data = await response.json();
        ready = response.ok && data.ready === true;
        const notice = parseNotice(data.service_notice);
        if (notice) serverNotice = notice;
      } catch { ready = false; }
    })()
  ]).then(publish).finally(() => { pending = null; });
  return pending;
}
export async function assertServiceAvailable() {
  if (get(serviceStore).phase === 'checking') await refreshService();
  const state = get(serviceStore);
  if (state.blocksNewRequests) throw new Error(state.notice?.message || '현재 새 작업을 시작할 수 없습니다. 잠시 후 다시 시도해 주세요.');
}
export function startServiceMonitor() {
  try { applyRemote(JSON.parse(localStorage.getItem(cacheKey))); } catch {}
  refreshService();
  const timer = setInterval(() => { publish(); refreshService(); }, 30000);
  const resume = () => { if (!document.hidden) refreshService(); };
  const noticeHandler = event => { serverNotice = parseNotice(event.detail) || serverNotice; publish(); };
  document.addEventListener('visibilitychange', resume);
  document.addEventListener('serviceNotice', noticeHandler);
  window.addEventListener('online', resume);
  return () => { clearInterval(timer); document.removeEventListener('visibilitychange', resume); document.removeEventListener('serviceNotice', noticeHandler); window.removeEventListener('online', resume); };
}
